import { useEffect, useState } from 'react';
import { useTheme } from '../../../../hooks/useTheme';
import ReactEDSWrapper from '../../../../utils/react-eds-wrapper';
import {
  useReactTable,
  getCoreRowModel,
  createColumnHelper,
  ColumnHelper,
  AccessorKeyColumnDef,
  flexRender,
  SortingState
} from '@tanstack/react-table';
import { Pagination, Tag } from '@dsi/react-eds';
import { useSwobNotifications } from '../../../../hooks/NotificationsProvider';
import { NiiReportContentResponse, RecordsType, ReportFiltersType } from './types/shared.types';
import ReportFilters from './ReportFilters';
import ReportDialog from './ReportDialog';
import { niiService } from '../../../../services/niiService';
import { AxiosError } from 'axios';
import styles from './ReportTable.module.css';
import { useLoader } from '../../../../hooks/useLoader';
import { LOADER_DEBOUNCE_MS } from '../../../../const/global-constants';

interface PropsObject {
  data: RecordsType[];
  reportId: string;
  reportTab?: string;
  report: NiiReportContentResponse;
  filters: ReportFiltersType[];
  type: string;
  clickable: boolean | undefined;
  columns: { attribute: string; title: string }[];
  metadataFields: { attribute: string; title: string }[];
  impactAssessmentFields: { attribute: string; title: string }[];
}

const columnHelper: ColumnHelper<RecordsType> = createColumnHelper<RecordsType>();
function ReportTable(props: Readonly<PropsObject>) {
  const { theme } = useTheme();
  const columnsDesc = props.columns;
  const filters = props.filters;
  const report = props.report;
  const [completeData, setCompleteData] = useState<NiiReportContentResponse>(props.report);
  const [data, setData] = useState<RecordsType[]>(props.data);
  const [filtersSelected, setFiltersSelected] = useState<Record<string, string>>({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [selectedData, setSelectedData] = useState<RecordsType>();
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10
  });
  const { showLoader, hideLoader } = useLoader();
  const { addNotifications } = useSwobNotifications();

  const getItemNumbers = () => {
    if (data) {
      return `${
        (completeData?.total || 0) > pagination.pageSize
          ? pagination.pageSize + pagination.pageIndex * pagination.pageSize
          : completeData?.total || 0
      } of ${completeData?.total || 0} items`;
    }
  };

  const fetchReportData = async (filters: string) => {
    try {
      showLoader();
      const res = await niiService.getReportContent(props.reportId, filters);
      const response: NiiReportContentResponse = res.data;
      setCompleteData(response);
      setData(response?.data || []);
      table.setPageSize(response.limit || 0);
      table.options.rowCount = response.total;
      table.options.pageCount = response.totalPages;
      console.log(table);
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred fetching report',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const handleChangesAndFetch = (
    filtersSelected: Record<string, string>,
    pagination: { pageIndex: number; pageSize: number },
    sorting: SortingState
  ) => {
    let filters = `page=${pagination.pageIndex + 1}&limit=${pagination.pageSize}`;
    if (props.reportTab) filters = filters + props.reportTab;
    if (filtersSelected && Object.keys(filtersSelected).length > 0) {
      Object.keys(filtersSelected).forEach((key) => {
        if (filtersSelected[key]) {
          filters = filters + `&${key}=${filtersSelected[key]}`;
        }
      });
    }
    if (sorting) {
      filters = filters + `&sortBy=${sorting[0]?.id || 'created_at'}&sortOrder=${sorting[0]?.desc ? 'DESC' : 'ASC'}`;
    }
    fetchReportData(filters);
  };

  const handleDownloadReport = () => {
    let filters = '';
    if (filtersSelected && Object.keys(filtersSelected).length > 0) {
      Object.keys(filtersSelected).forEach((key) => {
        if (filtersSelected[key]) {
          filters = filters + `&${key}=${filtersSelected[key]}`;
        }
      });
    }
    if (sorting) {
      filters = filters + `&sortBy=${sorting[0]?.id || 'created_at'}&sortOrder=${sorting[0]?.desc ? 'DESC' : 'ASC'}`;
    }
    downloadReport(filters);
  };

  const downloadReport = async (filters: string) => {
    try {
      showLoader();
      const res = await niiService.exportReport(props.reportId, filters);
      const blob = new Blob([res.data], { type: res.headers['content-type'] });
      const contentDisposition = res.headers['content-disposition'];
      const fileName = contentDisposition.split('filename=')[1].replace(/"/g, '');

      //Create a temporary URL
      const url = window.URL.createObjectURL(blob);
      //Trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      //Cleanup
      if (link?.parentNode) {
        link.parentNode.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      addNotifications([
        {
          title: 'Error',
          icon: 'icon-triangle-warning',
          description:
            error && error instanceof AxiosError ? error.response?.data?.message || error.message : 'An error occurred fetching report',
          timestamp: new Date(),
          color: 'red'
        }
      ]);
    } finally {
      setTimeout(() => hideLoader(), LOADER_DEBOUNCE_MS);
    }
  };

  const handleFilters = (values: Record<string, string>) => {
    setFiltersSelected(values);
    table.setPageIndex(0);
    handleChangesAndFetch(values, { ...pagination, pageIndex: 0 }, sorting);
  };

  const handleDialogClose = () => {
    setSelectedData(undefined);
    setOpenDialog(false);
  };

  const handleClickRow = (selectedRow: RecordsType) => {
    if (props.clickable) {
      setSelectedData(selectedRow);
      setOpenDialog(true);
    }
  };

  const truncateText = (text: string, maxWords: number = 100): string => {
    const words = text.split(/\s+/);
    if (words.length <= maxWords) return text;
    return words.slice(0, maxWords).join(' ') + '...';
  };

  const getCellValue = (row: RecordsType, attribute: string) => {
    const value = row[attribute];
    if (attribute === 'format_type') {
      return (
        <ReactEDSWrapper component={Tag} big color={value === 'FULL' ? 'blue' : 'green'}>
          {value}
        </ReactEDSWrapper>
      );
    } else if (Array.isArray(value)) {
      return <>{truncateText(value.join(', '))}</>;
    } else {
      return <>{typeof value === 'string' ? truncateText(value) : value}</>;
    }
  };

  const columns = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const columnsReturn: AccessorKeyColumnDef<any, any>[] = [];
    columnsDesc.forEach((col) => {
      columnsReturn.push(
        columnHelper.accessor(col.attribute, {
          header: col.title,
          cell: (info) => getCellValue(info.row.original, col.attribute),
          footer: (props) => props.column.id
        })
      );
    });
    return columnsReturn;
  };

  const table = useReactTable({
    data: data,
    columns: columns(),
    manualFiltering: true,
    manualSorting: true,
    manualPagination: true,
    enableSortingRemoval: true,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    /*debugTable: true,
            debugHeaders: true,
            debugColumns: false,*/
    state: {
      //...
      pagination,
      sorting
    },
    onSortingChange: (updater) => {
      const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
      setSorting(newSorting);
      handleChangesAndFetch(filtersSelected, { ...pagination, pageIndex: 0 }, newSorting);
      setPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  });

  useEffect(() => {
    if (report && table) {
      table.setPageSize(report.limit || 0);
      table.options.rowCount = report.total;
      table.options.pageCount = report.totalPages;
    }
  }, [report, table]);

  function getSortingState(sorted: string | boolean) {
    if (sorted === 'asc') {
      return <i className="icon icon-sort-up" style={{ transform: 'rotate(180deg)' }}></i>;
    } else if (sorted === 'desc') {
      return <i className="icon icon-sort-down"></i>;
    } else {
      return <i className="icon icon-sorting"></i>;
    }
  }

  return (
    <>
      <div>
        <ReportFilters report={report} filters={filters} transmitFilters={handleFilters} downloadReport={handleDownloadReport} />
        <br />

        <div>
          <span style={{ color: '#878787' }}>{getItemNumbers()}</span>
          <div className={`table ${styles.tableContainer}`}>
            <table
              style={{
                width: '100%',
                borderCollapse: 'collapse'
              }}
            >
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        style={{
                          textAlign: 'left',
                          padding: '12px 16px'
                        }}
                      >
                        <div // NOSONAR
                          className={header.column.getCanSort() ? styles.sortableHeader : styles.unsortableHeader}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className={styles.sortIcon}>{getSortingState(header.column.getIsSorted())}</span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              {data.length === 0 && (
                <tbody>
                  <tr>
                    <td colSpan={5}>
                      <div className={styles.noDataContainer}>
                        <i className={`icon icon-info ${styles.dangerIcon}`} />
                        <div className={styles.noDataTitle}>No Data Found</div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              )}
              {data?.length > 0 && (
                <tbody>
                  {table.getPaginationRowModel().rows.map((row) => (
                    <tr
                      key={row.id}
                      style={{
                        transition: 'background-color 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = !theme ? '#404040' : '#DCDCDC';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                      }}
                      onClick={() => handleClickRow(row.original)}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <td
                          key={cell.id}
                          style={{
                            padding: '12px 16px',
                            borderTop: `1px solid ${!theme ? '#404040' : '#242424'}`
                          }}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              )}
            </table>
          </div>
        </div>

        <ReactEDSWrapper
          component={Pagination}
          numPages={table.getPageCount()}
          selectedPage={table.getState().pagination.pageIndex + 1}
          onPaginationChangePage={(page: number) => {
            table.setPageIndex(page - 1);
            handleChangesAndFetch(filtersSelected, { ...pagination, pageIndex: page - 1 }, sorting);
          }}
          optionsList={[
            { value: '10', label: '10', active: table.getState().pagination.pageSize === 10 },
            { value: '20', label: '20', active: table.getState().pagination.pageSize === 20 },
            { value: '30', label: '30', active: table.getState().pagination.pageSize === 30 },
            { value: '40', label: '40', active: table.getState().pagination.pageSize === 40 },
            { value: '50', label: '50', active: table.getState().pagination.pageSize === 50 }
          ]}
          onPaginationChangeSelect={(size: string) => {
            table.setPageSize(Number(size));
            table.setPageIndex(0);
            handleChangesAndFetch(filtersSelected, { pageSize: Number(size), pageIndex: 0 }, sorting);
          }}
          withActions
        />
      </div>
      {selectedData && props.clickable && (
        <ReportDialog
          data={selectedData}
          type={props.type}
          metadataFields={props.metadataFields}
          impactAssessmentFields={props.impactAssessmentFields}
          open={openDialog}
          handleClose={handleDialogClose}
        />
      )}
    </>
  );
}
export default ReportTable;
